import React, { useState } from "react";
import { container } from "../styles/commonStyles";

const PreviewScreen = ({
  theme,
  goHome,
  words,
  gameType,
  setQuestionStartTime,
  setStep
}) => {
  const [viewMode, setViewMode] = useState("smart"); // "smart" or "quick"
  const [currentIndex, setCurrentIndex] = useState(0);
  const [expandedIndex, setExpandedIndex] = useState(null);
  const isDark = theme === "dark";
  const isTwins = gameType === "twins";

  const colors = {
    background: isDark ? "#0F172A" : "#F8FAFC",
    surface: isDark ? "#1E293B" : "#FFFFFF",
    text: isDark ? "#F1F5F9" : "#1E293B",
    secondaryText: isDark ? "#94A3B8" : "#64748B",
    accentMuted: isDark ? "#334155" : "#F1F5F9",
    border: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.04)"
  };

  const currentWord = words[currentIndex];

  const handleNext = () => {
    if (currentIndex < words.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const handlePrev = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const handleStart = () => {
    setQuestionStartTime(Date.now()); // Restart timer for fair start
    setStep("game");
  };

  const SynonymArrow = ({ size = 20 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="#14B8A6" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
      <path d="M18 8L22 12L18 16" />
      <path d="M6 8L2 12L6 16" />
      <line x1="2" y1="12" x2="22" y2="12" />
    </svg>
  );
  
  const ClashArrow = ({ size = 20 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="#EF4444" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
      <path d="M18 8L22 12L18 16" />
      <path d="M6 8L2 12L6 16" />
      <line x1="2" y1="12" x2="22" y2="12" />
    </svg>
  );

  return (
    <div style={{
      ...container,
      justifyContent: "flex-start",
      padding: "16px 20px 40px",
      minHeight: "100dvh",
      height: "auto",
      overflowY: "auto",
      background: colors.background,
      gap: "0",
      display: "flex",
      flexDirection: "column",
      alignItems: "center"
    }}>
      {/* Header */}
      <div style={{
        width: "100%", maxWidth: "420px", display: "flex", alignItems: "center",
        justifyContent: "space-between", marginBottom: "20px", marginTop: "16px"
      }}>
        <button
          onClick={() => setStep("input")}
          style={{
            width: "44px", height: "44px", borderRadius: "14px",
            background: colors.surface, border: isDark ? "1px solid #334155" : "none", display: "flex",
            alignItems: "center", justifyContent: "center", cursor: "pointer",
            boxShadow: isDark ? "0 4px 12px rgba(0,0,0,0.2)" : "0 4px 12px rgba(0,0,0,0.05)", color: colors.text
          }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6"></polyline>
          </svg>
        </button>
        <div style={{ textAlign: "center" }}>
          <h1 style={{ margin: 0, fontSize: "20px", fontWeight: "800", color: colors.text, display: "flex", alignItems: "center", gap: "8px", justifyContent: "center" }}>
            <span style={{ color: "#14B8A6" }}>✨</span> Learn & Play <span style={{ color: "#14B8A6" }}>✨</span>
          </h1>
          <p style={{ margin: "4px 0 0", fontSize: "12px", color: colors.secondaryText, fontWeight: "500" }}>
            Preview the words and play to earn points!
          </p>
        </div>
        <div style={{ width: "44px" }} /> {/* Spacer */}
      </div>

      {/* Tab Toggle */}
      <div style={{
        width: "100%", maxWidth: "420px", background: colors.surface, borderRadius: "20px",
        padding: "6px", display: "flex", gap: "6px", marginBottom: "24px",
        boxShadow: isDark ? "0 10px 25px rgba(0,0,0,0.2)" : "0 10px 25px rgba(0,0,0,0.04)",
        border: isDark ? "1px solid #334155" : "none"
      }}>
        <button
          onClick={() => setViewMode("smart")}
          style={{
            flex: 1, padding: "12px", borderRadius: "14px", border: "none",
            background: viewMode === "smart" ? "linear-gradient(135deg, #14B8A6, #0D9488)" : "transparent",
            color: viewMode === "smart" ? "white" : colors.secondaryText, cursor: "pointer",
            display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
            transition: "all 0.3s ease", fontWeight: "700"
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
            </svg>
            <div style={{ textAlign: "left" }}>
              <div style={{ fontSize: "14px" }}>Smart Learn</div>
              <div style={{ fontSize: "10px", opacity: 0.8 }}>Card View</div>
            </div>
          </div>
        </button>
        <button
          onClick={() => setViewMode("quick")}
          style={{
            flex: 1, padding: "12px", borderRadius: "14px", border: "none",
            background: viewMode === "quick" ? "linear-gradient(135deg, #14B8A6, #0D9488)" : "transparent",
            color: viewMode === "quick" ? "white" : colors.secondaryText, cursor: "pointer",
            display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
            transition: "all 0.3s ease", fontWeight: "700"
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="8" y1="6" x2="21" y2="6"></line>
              <line x1="8" y1="12" x2="21" y2="12"></line>
              <line x1="8" y1="18" x2="21" y2="18"></line>
            </svg>
            <div style={{ textAlign: "left" }}>
              <div style={{ fontSize: "14px" }}>Quick Scan</div>
              <div style={{ fontSize: "10px", opacity: 0.8 }}>List View</div>
            </div>
          </div>
        </button>
      </div>

      {/* Content Area */}
      <div style={{ width: "100%", maxWidth: "420px", flex: 1, marginBottom: "20px" }}>
        {viewMode === "smart" ? (
          /* Smart Learn Card View */
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
            {/* Progress Header */}
            <div style={{
              width: "100%", background: colors.surface, borderRadius: "24px", padding: "20px",
              display: "flex", alignItems: "center", gap: "16px", marginBottom: "20px",
              boxShadow: isDark ? "0 8px 20px rgba(0,0,0,0.15)" : "0 8px 20px rgba(0,0,0,0.03)", position: "relative",
              textAlign: "left", border: isDark ? "1px solid #334155" : "none"
            }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: "10px", color: colors.secondaryText, fontWeight: "700", textTransform: "uppercase", letterSpacing: "1px", marginBottom: "4px" }}>Your Progress</div>
                <div style={{ display: "flex", alignItems: "baseline", gap: "4px" }}>
                  <span style={{ fontSize: "24px", fontWeight: "800", color: colors.text }}>{currentIndex + 1}</span>
                  <span style={{ fontSize: "16px", fontWeight: "600", color: colors.secondaryText }}>/ {words.length}</span>
                </div>
                <div style={{ fontSize: "12px", color: colors.secondaryText, fontWeight: "500", marginTop: "2px" }}>words learned</div>
                {/* Mini Progress Bar */}
                <div style={{ width: "100%", height: "6px", background: colors.accentMuted, borderRadius: "3px", marginTop: "12px", overflow: "hidden" }}>
                  <div style={{ width: `${((currentIndex + 1) / words.length) * 100}%`, height: "100%", background: "#14B8A6", transition: "width 0.4s ease" }} />
                </div>
              </div>
              <div style={{ width: "80px", height: "80px", position: "relative" }}>
                <img src="/images/game_s/brain_reading.webp" alt="Mascot" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
                <div style={{ position: "absolute", bottom: "-10px", left: "0", right: "0", textAlign: "center", fontSize: "10px", fontWeight: "700", color: "#14B8A6" }}>Keep it up!</div>
              </div>
            </div>

            {/* Main Word Card */}
            <div style={{
              width: "100%", background: colors.surface, borderRadius: "28px", padding: "40px 24px",
              display: "flex", flexDirection: "column", alignItems: "center",
              boxShadow: isDark ? "0 15px 35px rgba(0,0,0,0.2)" : "0 15px 35px rgba(0,0,0,0.05)", marginBottom: "20px", position: "relative",
              border: isDark ? "1px solid #334155" : "none"
            }}>
              <div style={{ position: "absolute", top: "20px", left: "20px", background: colors.accentMuted, padding: "4px 10px", borderRadius: "8px", fontSize: "12px", fontWeight: "700", color: colors.secondaryText }}>
                {currentIndex + 1} / {words.length}
              </div>

              {isTwins ? (
                <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", alignItems: "center", gap: "12px", margin: "20px 0 12px" }}>
                  <h2 style={{ fontSize: "36px", fontWeight: "800", color: colors.text, margin: 0, textAlign: "center" }}>{currentWord.word1 || currentWord.wordObj.word}</h2>
                  <SynonymArrow size={24} />
                  <h2 style={{ fontSize: "36px", fontWeight: "800", color: colors.text, margin: 0, textAlign: "center" }}>{currentWord.word2 || currentWord.relatedWordObj.word}</h2>
                </div>
              ) : (
                <h2 style={{ fontSize: "42px", fontWeight: "800", color: colors.text, margin: "20px 0 12px", textAlign: "center" }}>{currentWord.word}</h2>
              )}
              
              <p style={{ fontSize: "18px", color: colors.secondaryText, fontWeight: "500", textAlign: "center", margin: "0 0 40px", lineHeight: "1.5" }}>
                {isTwins ? (
                  <span style={{ display: "block" }}>
                    <strong>{currentWord.word1 || currentWord.wordObj.word}:</strong> {currentWord.meaning1 || currentWord.wordObj.meaning}<br/>
                    <strong>{currentWord.word2 || currentWord.relatedWordObj.word}:</strong> {currentWord.meaning2 || currentWord.relatedWordObj.meaning}
                  </span>
                ) : currentWord.meaning}
              </p>

              {/* Connotation Badge if needed */}
              {gameType === "connotation" && currentWord.connotation && (
                <div style={{
                  background: colors.accentMuted, padding: "16px", borderRadius: "18px", width: "100%",
                  display: "flex", alignItems: "center", gap: "12px", marginBottom: "40px",
                  textAlign: "left"
                }}>
                   <div style={{ fontSize: "20px" }}>💡</div>
                   <div style={{ fontSize: "13px", color: colors.text, fontWeight: "500", lineHeight: "1.4" }}>
                     Think: This word has a <span style={{ color: currentWord.connotation.includes("positive") ? "#10B981" : "#EF4444", fontWeight: "700" }}>{currentWord.connotation.includes("positive") ? "positive" : "negative"}</span> connotation.
                   </div>
                </div>
              )}

              <div style={{ display: "flex", width: "100%", gap: "12px" }}>
                {currentIndex > 0 && (
                  <button
                    onClick={handlePrev}
                    style={{
                      flex: 1, background: colors.accentMuted,
                      color: colors.text, border: "none",
                      borderRadius: "18px", padding: "16px", fontSize: "16px", fontWeight: "700",
                      display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
                      cursor: "pointer", transition: "all 0.3s ease"
                    }}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="15 18 9 12 15 6"></polyline>
                    </svg>
                    Prev
                  </button>
                )}
                
                <button
                  onClick={currentIndex === words.length - 1 ? handleStart : handleNext}
                  style={{
                    flex: 2, background: "#0D9488",
                    color: "white", border: "none",
                    borderRadius: "18px", padding: "16px", fontSize: "16px", fontWeight: "700",
                    display: "flex", alignItems: "center", justifyContent: "center", gap: "10px",
                    cursor: "pointer", transition: "all 0.3s ease",
                    boxShadow: "0 8px 20px rgba(13, 148, 136, 0.2)"
                  }}
                >
                  {currentIndex === words.length - 1 ? "Start Game" : "Next Word"}
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="9 18 15 12 9 6"></polyline>
                  </svg>
                </button>
              </div>
            </div>

            {/* Dots */}
            <div style={{ display: "flex", gap: "6px", justifyContent: "center", marginBottom: "30px" }}>
              {words.map((_, i) => (
                <div key={i} style={{
                  width: i === currentIndex ? "24px" : "8px", height: "8px",
                  borderRadius: "4px", background: i === currentIndex ? "#14B8A6" : colors.accentMuted,
                  transition: "all 0.3s ease"
                }} />
              ))}
            </div>
          </div>
        ) : (
          /* Quick Scan List View */
          <div style={{ display: "flex", flexDirection: "column" }}>
            {/* List Header */}
            <div style={{
              width: "100%", background: isDark ? "linear-gradient(135deg, #1e293b, #0f172a)" : "linear-gradient(135deg, #F0FDFA, #E0F2FE)",
              borderRadius: "24px", padding: "20px", display: "flex", alignItems: "center",
              marginBottom: "20px", boxShadow: isDark ? "0 8px 20px rgba(0,0,0,0.15)" : "0 8px 20px rgba(0,0,0,0.02)",
              textAlign: "left", border: isDark ? "1px solid #334155" : "none"
            }}>
              <div style={{ flex: 1 }}>
                <h3 style={{ margin: 0, fontSize: "18px", fontWeight: "800", color: colors.text, display: "flex", alignItems: "center", gap: "8px" }}>
                  Quick Scan Mode <span style={{ color: "#14B8A6" }}>⚡</span>
                </h3>
                <p style={{ margin: "4px 0 0", fontSize: "12px", color: colors.secondaryText, fontWeight: "500" }}>
                  Take a quick look at all {words.length} words before the challenge!
                </p>
              </div>
            </div>

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px", padding: "0 4px" }}>
              <div style={{ fontSize: "11px", fontWeight: "800", color: colors.secondaryText, textTransform: "uppercase", letterSpacing: "0.5px" }}>{words.length} Words to Play</div>
              <div style={{ background: colors.surface, padding: "4px 8px", borderRadius: "8px", fontSize: "11px", fontWeight: "700", color: colors.secondaryText, display: "flex", alignItems: "center", gap: "4px", boxShadow: "0 2px 8px rgba(0,0,0,0.02)", border: isDark ? "1px solid #334155" : "none" }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                </svg>
                {words.length} Words
              </div>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: "10px", marginBottom: "30px" }}>
              {words.map((w, i) => {
                const isExpanded = expandedIndex === i;
                return (
                  <div key={i} 
                    onClick={() => {
                      if (isTwins) {
                        setExpandedIndex(isExpanded ? null : i);
                      }
                    }}
                    style={{
                      background: colors.surface, borderRadius: "18px", padding: "14px 16px",
                      display: "flex", flexDirection: "column", gap: "8px",
                      boxShadow: isDark ? "0 4px 12px rgba(0,0,0,0.1)" : "0 4px 12px rgba(0,0,0,0.03)", textAlign: "left",
                      border: isDark ? (isExpanded ? "1px solid #14B8A6" : "1px solid #334155") : (isExpanded ? "1px solid #14B8A6" : "none"),
                      cursor: isTwins ? "pointer" : "default",
                      transition: "all 0.3s ease"
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
                      <div style={{
                        width: "32px", height: "32px", background: isDark ? "rgba(20, 184, 166, 0.1)" : "#F0FDFA", color: "#14B8A6",
                        borderRadius: "10px", display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: "14px", fontWeight: "800", flexShrink: 0
                      }}>
                        {i + 1}
                      </div>
                      <div style={{ flex: 1 }}>
                        {isTwins ? (
                          <div style={{ display: "flex", alignItems: "center", gap: "10px", justifyContent: "space-between" }}>
                            <div style={{ fontSize: "15px", fontWeight: "800", color: colors.text, display: "flex", alignItems: "center", gap: "8px" }}>
                              {w.word1 || w.wordObj.word} <SynonymArrow size={16} /> {w.word2 || w.relatedWordObj.word}
                            </div>
                            <svg 
                              width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={colors.secondaryText} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"
                              style={{ transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.3s ease" }}
                            >
                              <polyline points="6 9 12 15 18 9"></polyline>
                            </svg>
                          </div>
                        ) : (
                          <>
                            <div style={{ fontSize: "15px", fontWeight: "800", color: colors.text }}>{w.word}</div>
                            <div style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "2px" }}>
                              <div style={{ fontSize: "12px", color: colors.secondaryText, fontWeight: "500" }}>{w.meaning}</div>
                              {gameType === "connotation" && w.connotation && (
                                <div style={{
                                  fontSize: "9px",
                                  fontWeight: "800",
                                  textTransform: "uppercase",
                                  color: w.connotation.includes("positive") ? "#10B981" : "#EF4444",
                                  background: w.connotation.includes("positive") ? "rgba(16, 185, 129, 0.1)" : "rgba(239, 68, 68, 0.1)",
                                  padding: "2px 6px",
                                  borderRadius: "4px",
                                  letterSpacing: "0.5px",
                                  flexShrink: 0
                                }}>
                                  {w.connotation.includes("positive") ? "Positive" : "Negative"}
                                </div>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    </div>

                    {isTwins && isExpanded && (
                      <div style={{ 
                        marginTop: "4px", padding: "12px", background: colors.accentMuted, borderRadius: "12px",
                        animation: "fadeIn 0.2s ease-out"
                      }}>
                        <div style={{ marginBottom: "8px" }}>
                          <span style={{ fontWeight: "800", color: colors.text, fontSize: "13px" }}>{w.word1 || w.wordObj.word}:</span>
                          <span style={{ color: colors.secondaryText, fontSize: "13px", marginLeft: "6px" }}>{w.meaning1 || w.wordObj.meaning}</span>
                        </div>
                        <div>
                          <span style={{ fontWeight: "800", color: colors.text, fontSize: "13px" }}>{w.word2 || w.relatedWordObj.word}:</span>
                          <span style={{ color: colors.secondaryText, fontSize: "13px", marginLeft: "6px" }}>{w.meaning2 || w.relatedWordObj.meaning}</span>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Footer CTA */}
      <div style={{
        width: "100%", maxWidth: "420px", background: colors.surface, borderRadius: "24px",
        padding: "16px", display: "flex", alignItems: "center", gap: "12px",
        boxShadow: isDark ? "0 -10px 25px rgba(0,0,0,0.2)" : "0 -10px 25px rgba(0,0,0,0.03)", marginTop: "auto",
        border: isDark ? "1px solid #334155" : "none"
      }}>
        <div style={{ width: "64px", height: "64px", background: isDark ? "rgba(20, 184, 166, 0.1)" : "#F0FDFA", borderRadius: "18px", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <img src="/images/game_s/controller.webp" alt="Controller" style={{ width: "54px", height: "54px", objectFit: "contain" }} />
        </div>
        <div style={{ flex: 1, textAlign: "left" }}>
          <div style={{ fontSize: "15px", fontWeight: "800", color: colors.text }}>Ready to Play?</div>
          <div style={{ fontSize: "12px", color: colors.secondaryText, fontWeight: "500" }}>You'll play with these {words.length} words</div>
        </div>
        <button
          onClick={handleStart}
          style={{
            background: "#0D9488", color: "white", border: "none", borderRadius: "14px",
            padding: "12px 16px", fontSize: "14px", fontWeight: "700", cursor: "pointer",
            display: "flex", alignItems: "center", gap: "6px", boxShadow: "0 8px 16px rgba(13, 148, 136, 0.2)"
          }}
        >
          Start Game
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="9 18 15 12 9 6"></polyline>
          </svg>
        </button>
      </div>
    </div>
  );
};

export default PreviewScreen;

